import type { ApiSuccess, Paginated } from './api';

/**
 * Surveys staff type in after a respondent answered on paper, in person or by phone.
 *
 * A survey is a definition — its bilingual questions, their options and the rules between them —
 * stored as data and versioned, so a new questionnaire needs no table or screen of its own.
 * Answers are kept by question key and option code, never by label, so they count the same
 * whichever language they were typed in.
 *
 * The backend loads this file at runtime, so it must not import anything but types.
 */

export const SURVEY_LANGUAGES = ['en', 'ar'] as const;
export type SurveyLanguage = (typeof SURVEY_LANGUAGES)[number];

/** Text in each language the survey is offered in. A definition must fill every one it lists. */
export type SurveyText = Partial<Record<SurveyLanguage, string>>;

/** How the answers reached staff. Belongs to the response, not to any questionnaire. */
export const SURVEY_CHANNELS = ['paper', 'in-person', 'phone'] as const;
export type SurveyChannel = (typeof SURVEY_CHANNELS)[number];

/**
 * `single` is one pick and `multiple` several, both from `options`; the rest are typed. `phone`
 * is a text answer with enough digits to dial. Length limits live in `@mds/shared/lib/survey`.
 */
export const SURVEY_QUESTION_TYPES = [
  'single',
  'multiple',
  'text',
  'textarea',
  'phone',
  'date',
] as const;
export type SurveyQuestionType = (typeof SURVEY_QUESTION_TYPES)[number];

/** A survey's key, as it appears in the URL: `business-needs`. */
export const SURVEY_KEY_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
/** A question's key, which is also its answer's key: `trainingReadiness`. */
export const SURVEY_QUESTION_KEY_PATTERN = /^[a-z][A-Za-z0-9]*$/;
/** An option's stored code: `train-staff`, `2-5`, `20+`. */
export const SURVEY_OPTION_CODE_PATTERN = /^[a-z0-9][a-z0-9+-]*$/;

export interface SurveyOption {
  /** What is stored and counted. Never changes once responses use it. */
  value: string;
  label: SurveyText;
  /** "None of these" on a `multiple` question: it is ticked alone or not at all. */
  exclusive?: boolean;
}

/** Holds when the answer to an earlier choice question includes any of these codes. */
export interface SurveyCondition {
  question: string;
  anyOf: string[];
}

export interface SurveyQuestion {
  kind: 'question';
  key: string;
  type: SurveyQuestionType;
  /** The number printed beside it on the form, e.g. `4`. Follow-ups have none. */
  number?: string;
  label: SurveyText;
  /** A word or two for column headings and exports: "Sector" for "What field do you work in?". */
  shortLabel?: SurveyText;
  hint?: SurveyText;
  required: boolean;
  /** `single` and `multiple` only. */
  options?: SurveyOption[];
  /** `multiple` only: the most boxes that may be ticked. */
  maxSelections?: number;
  /** Typed questions only. Defaults to `surveyMaxLength` for its type. */
  maxLength?: number;
  /** Asked only when this holds. Otherwise its answer is dropped, not stored. */
  showIf?: SurveyCondition;
}

/** Questions set apart in a box, usually because only some respondents are asked them. */
export interface SurveyGroup {
  kind: 'group';
  key: string;
  number?: string;
  title?: SurveyText;
  description?: SurveyText;
  /** Applies to every question in the group, on top of each question's own. */
  showIf?: SurveyCondition;
  questions: SurveyQuestion[];
}

export type SurveyItem = SurveyQuestion | SurveyGroup;

export interface SurveySection {
  key: string;
  title: SurveyText;
  description?: SurveyText;
  items: SurveyItem[];
}

/** A count shown above the responses, and the tag on each response it covers. */
export interface SurveyHighlight {
  key: string;
  label: SurveyText;
  note?: SurveyText;
  /** Tag on each matching response in the list. Omit to count without tagging. */
  tag?: SurveyText;
  when: SurveyCondition;
}

export interface SurveyListing {
  /** The question whose answer names a response in the list. Blank reads as anonymous. */
  title?: string;
  /** Questions shown as columns after the title, in order. */
  columns: string[];
  /** Heading over the column of highlight tags. Omit to leave the column out. */
  tagsLabel?: SurveyText;
}

export interface SurveyDefinition {
  /** The languages it can be filled in. The first is the one the form opens in. */
  languages: SurveyLanguage[];
  title: SurveyText;
  intro?: SurveyText;
  sections: SurveySection[];
  highlights: SurveyHighlight[];
  listing: SurveyListing;
  /**
   * The `phone` or `text` question that identifies a respondent. Responses sharing an answer to
   * it are flagged as repeats, never refused.
   */
  identity?: string;
}

/** One answer: the code picked, the codes ticked, or the text typed. */
export type SurveyAnswer = string | string[];

/** Answers by question key. A question with no answer is left out. */
export type SurveyAnswers = Record<string, SurveyAnswer>;

export const SURVEY_ISSUE_CODES = [
  'unknown_question',
  'invalid_type',
  'required',
  'invalid_option',
  'duplicate_option',
  'exclusive',
  'too_many',
  'too_long',
  'invalid_phone',
  'invalid_date',
] as const;
export type SurveyIssueCode = (typeof SURVEY_ISSUE_CODES)[number];

/**
 * Why an answer was refused. `path` is `['answers', questionKey]`, the shape of a Zod issue, so the
 * API's `details` and the form's own checks name a field the same way.
 */
export interface SurveyIssue {
  path: string[];
  code: SurveyIssueCode;
  message: string;
}

/** What `POST …/responses` takes. `PATCH` takes any of it; `answers` replaces them all. */
export interface SurveyResponseInput {
  /** `YYYY-MM-DD`: the day the respondent answered, not the day it was typed in. */
  collectedOn: string;
  channel: SurveyChannel;
  /** The language staff typed it in. One of the definition's `languages`. */
  language: SurveyLanguage;
  answers: SurveyAnswers;
}

export interface SurveyActor {
  id: string | null;
  name: string;
}

export interface AdminSurveyResponse extends SurveyResponseInput {
  id: string;
  surveyKey: string;
  /** The version of the questionnaire it was last saved against. Saving moves it to the latest. */
  surveyVersion: number;
  /** How many other responses share its `identity` answer. 0 when there is none. */
  repeats: number;
  enteredBy: SurveyActor;
  updatedBy: SurveyActor | null;
  createdAt: string;
  updatedAt: string;
}

export interface AdminSurveyListItem {
  key: string;
  title: SurveyText;
  version: number;
  responseCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface AdminSurvey extends AdminSurveyListItem {
  definition: SurveyDefinition;
}

export interface CreateSurveyInput {
  key: string;
  definition: SurveyDefinition;
}

export interface PublishSurveyInput {
  definition: SurveyDefinition;
}

export interface SurveyVersion {
  surveyKey: string;
  version: number;
  definition: SurveyDefinition;
  publishedAt: string;
}

export interface SurveyResponseListQuery {
  page?: number;
  limit?: number;
  /** Matches typed answers: names, phone numbers, free text. */
  search?: string;
  /** A highlight's key: only the responses it counts. */
  highlight?: string;
  /** Comma-separated `question:code` pairs, every one of which must match. */
  answers?: string;
}

export interface SurveySummary {
  total: number;
  /** By highlight key. */
  highlights: Record<string, number>;
  /** By choice question, then option code: how many responses picked it. */
  counts: Record<string, Record<string, number>>;
}

export type AdminSurveyListResponse = ApiSuccess<AdminSurveyListItem[]>;
export type AdminSurveyDetailResponse = ApiSuccess<AdminSurvey>;
export type SurveyVersionResponse = ApiSuccess<SurveyVersion>;
export type SurveySummaryResponse = ApiSuccess<SurveySummary>;
export type SurveyResponseList = Paginated<AdminSurveyResponse>;
/** The envelope around one survey response. */
export type AdminSurveyResponseResponse = ApiSuccess<AdminSurveyResponse>;
